import { useCallback, useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import {
  Check,
  ChevronsUpDown,
  Download,
  KeyRound,
  Loader2,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAdminFetch } from "@/hooks/use-admin-fetch";

/**
 * Admin → Compliance → 2FA enrollment.
 *
 * Port of `src/app/admin/compliance/2fa/page.tsx` (legacy NextJS, ~390 LOC) per D21.
 *
 * Endpoints:
 *   - GET  /api/admin/compliance/2fa
 *   - POST /api/admin/compliance/2fa/reset { userId, reason }
 */

interface Row {
  userId: string;
  email: string;
  name: string;
  role: "provider" | "clinic_admin" | "clinic_staff" | "superadmin";
  twoFactorEnabled: boolean;
  enrolledAt: string | null;
  lastUsedAt: string | null;
  lastLoginAt: string | null;
}

interface Summary {
  total: number;
  enabled: number;
  disabled: number;
  complianceRate: number;
}

type Filter = "all" | "enabled" | "disabled";

const roleLabels: Record<Row["role"], string> = {
  provider: "Provider",
  clinic_admin: "Clinic admin",
  clinic_staff: "Clinic staff",
  superadmin: "Superadmin",
};

export const Route = createFileRoute("/_authenticated/compliance/2fa")({
  component: TwoFactorCompliancePage,
});

function TwoFactorCompliancePage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [resetTarget, setResetTarget] = useState<Row | null>(null);
  const [resetReason, setResetReason] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetch.get<{ rows: Row[]; summary: Summary }>(
        "/api/admin/compliance/2fa",
      );
      setRows(data.rows);
      setSummary(data.summary);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "enabled" && !r.twoFactorEnabled) return false;
      if (filter === "disabled" && r.twoFactorEnabled) return false;
      if (!q) return true;
      return (
        r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q) || r.role.includes(q)
      );
    });
  }, [rows, filter, query]);

  const submitReset = useCallback(async () => {
    if (!resetTarget) return;
    if (resetReason.trim().length < 10) {
      setResetError(t`Reason must be at least 10 characters (audit log).`);
      return;
    }
    setResetSubmitting(true);
    setResetError(null);
    try {
      await adminFetch.post("/api/admin/compliance/2fa/reset", {
        userId: resetTarget.userId,
        reason: resetReason.trim(),
      });
      setResetTarget(null);
      setResetReason("");
      await load();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | undefined)?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : "Reset failed";
      setResetError(msg);
    } finally {
      setResetSubmitting(false);
    }
  }, [adminFetch, load, resetReason, resetTarget, t]);

  const exportCsv = useCallback(() => {
    if (!rows) return;
    const header = [
      "email",
      "name",
      "role",
      "twoFactorEnabled",
      "enrolledAt",
      "lastUsedAt",
      "lastLoginAt",
    ];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push(
        [
          r.email,
          `"${r.name.replace(/"/g, '""')}"`,
          r.role,
          r.twoFactorEnabled ? "yes" : "no",
          r.enrolledAt ?? "",
          r.lastUsedAt ?? "",
          r.lastLoginAt ?? "",
        ].join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pontimed-2fa-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Shield className="text-primary size-6" />
        <div>
          <h1 className="text-foreground text-2xl font-bold">
            <Trans>2FA Compliance</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>Monitor 2FA enrollment for professional accounts (NIS2 + GDPR Art. 32)</Trans>
          </p>
        </div>
      </div>

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard
            label={t`Total pro users`}
            value={summary.total}
            icon={<Shield className="size-4" />}
          />
          <SummaryCard
            label={t`2FA active`}
            value={summary.enabled}
            icon={<ShieldCheck className="size-4" />}
            color="emerald"
          />
          <SummaryCard
            label={t`2FA missing`}
            value={summary.disabled}
            icon={<ShieldAlert className="size-4" />}
            color={summary.disabled > 0 ? "amber" : "emerald"}
          />
          <SummaryCard
            label={t`Compliance`}
            value={`${summary.complianceRate}%`}
            icon={<ChevronsUpDown className="size-4" />}
            color={
              summary.complianceRate === 100
                ? "emerald"
                : summary.complianceRate >= 80
                  ? "amber"
                  : "red"
            }
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t`Search email, name...`}
            className="border-border bg-background focus:border-primary focus:ring-primary/20 w-full rounded-lg border py-2 pr-3 pl-9 text-sm outline-none focus:ring-2"
          />
        </div>

        <div className="bg-muted flex gap-1 rounded-lg p-1">
          {(["all", "enabled", "disabled"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={
                filter === f
                  ? "bg-card text-foreground rounded-md px-3 py-1.5 text-xs font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs font-medium"
              }
            >
              {f === "all" ? (
                <Trans>All</Trans>
              ) : f === "enabled" ? (
                <Trans>Active</Trans>
              ) : (
                <Trans>Missing</Trans>
              )}
            </button>
          ))}
        </div>

        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows?.length}>
          <Download className="size-4" />
          <span className="ml-1.5">
            <Trans>Export CSV</Trans>
          </span>
        </Button>
      </div>

      <div className="border-border bg-card overflow-x-auto rounded-xl border">
        {loading ? (
          <div className="p-12 text-center">
            <div className="bg-muted mx-auto h-4 w-24 animate-pulse rounded" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-muted-foreground p-12 text-center text-sm">
            {rows?.length === 0 ? (
              <Trans>No pro users in the database yet.</Trans>
            ) : (
              <Trans>No users match your filters.</Trans>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-border border-b">
              <tr className="text-muted-foreground text-left text-xs tracking-wider uppercase">
                <th className="px-4 py-3 font-medium">
                  <Trans>Name</Trans>
                </th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">
                  <Trans>Email</Trans>
                </th>
                <th className="px-4 py-3 font-medium">
                  <Trans>Role</Trans>
                </th>
                <th className="px-4 py-3 text-center font-medium">2FA</th>
                <th className="hidden px-4 py-3 font-medium lg:table-cell">
                  <Trans>Enrolled</Trans>
                </th>
                <th className="hidden px-4 py-3 font-medium lg:table-cell">
                  <Trans>Last used</Trans>
                </th>
                <th className="px-4 py-3 text-right font-medium">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.userId} className="border-border border-b last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="text-foreground font-medium">{r.name || "—"}</div>
                    <div className="text-muted-foreground text-xs md:hidden">{r.email}</div>
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <span className="text-muted-foreground">{r.email}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-xs">
                      {roleLabels[r.role]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.twoFactorEnabled ? (
                      <Check className="mx-auto size-4 text-emerald-500" />
                    ) : (
                      <X className="mx-auto size-4 text-amber-500" />
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-xs lg:table-cell">
                    {r.enrolledAt ? new Date(r.enrolledAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-xs lg:table-cell">
                    {r.lastUsedAt ? new Date(r.lastUsedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.twoFactorEnabled && (
                      <button
                        onClick={() => {
                          setResetTarget(r);
                          setResetReason("");
                          setResetError(null);
                        }}
                        className="text-destructive hover:bg-destructive/10 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
                      >
                        <KeyRound className="size-3" />
                        <Trans>Reset</Trans>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="text-destructive size-5" />
              <Trans>Reset 2FA for {resetTarget?.name || resetTarget?.email}</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                This wipes the user's 2FA secret, recovery codes, and all active sessions. The user
                must re-enroll from scratch and log in again on every device. Use only after
                verifying identity (CUIM / photo ID / clinic contract).
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-3">
            <div>
              <label className="text-foreground mb-1 block text-xs font-medium">
                <Trans>Reason (logged for audit, min 10 chars)</Trans>
              </label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                rows={3}
                placeholder={t`e.g. Provider lost phone, ID verified via video call + clinic contract on file`}
                className="border-border bg-background focus:border-primary focus:ring-primary/20 w-full rounded-lg border p-3 text-sm outline-none focus:ring-2"
              />
            </div>

            {resetError && <p className="text-destructive text-sm">{resetError}</p>}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setResetTarget(null)}
                disabled={resetSubmitting}
                className="flex-1"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                variant="destructive"
                onClick={submitReset}
                disabled={resetSubmitting || resetReason.trim().length < 10}
                className="flex-1"
              >
                {resetSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trans>Reset 2FA</Trans>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  color = "default",
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color?: "default" | "emerald" | "amber" | "red";
}) {
  const colorClass =
    color === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : color === "amber"
        ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
        : color === "red"
          ? "bg-red-500/10 text-red-600 dark:text-red-400"
          : "bg-muted text-muted-foreground";
  return (
    <div className="border-border bg-card rounded-xl border p-4">
      <div className="flex items-start justify-between">
        <span className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
          {label}
        </span>
        <div className={`${colorClass} flex size-7 items-center justify-center rounded-md`}>
          {icon}
        </div>
      </div>
      <p className="text-foreground mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}
